import React, { useEffect } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import ContactSection from "../ContactSection";
import Lenis from "@studio-freight/lenis";
import {
  FileText,
  UserPlus,
  GitBranch,
  Calendar,
  Briefcase,
  Cpu,
  ArrowRight
} from "lucide-react";
import "../styles/website.css";
import "../styles/ProductPages.css";

const HiringHub = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const features = [
    {
      title: "Job Posting & Careers Portal",
      description: "Create and publish job posts to multiple networks and build a premium custom career site with zero code.",
      icon: <Briefcase size={22} />
    },
    {
      title: "AI Resume Screening",
      description: "Automatically parse resumes, extract work experience, and rank candidates against specific job specifications.",
      icon: <Cpu size={22} />
    },
    {
      title: "Visual Candidate Pipeline",
      description: "Track candidate pipelines through clear stages from screening, assessment, and interviews to final onboarding.",
      icon: <GitBranch size={22} />
    },
    {
      title: "Interview Scheduling",
      description: "Sync recruiters' Google/Outlook calendars, coordinate interview panels, and automate reminders for candidates.",
      icon: <Calendar size={22} />
    },
    {
      title: "Offer & Document Management",
      description: "Generate official job offers and appraisal agreements with digital signature capabilities and secure logs.",
      icon: <FileText size={22} />
    },
    {
      title: "Resume Parsing API",
      description: "Parse unstructured PDF or DOC resume files into well-structured JSON contact fields in milliseconds.",
      icon: <UserPlus size={22} />
    }
  ];

  return (
    <div className="prod-page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="prod-hero-section">
        <div className="prod-hero-glow"></div>
        <div className="prod-hero-left">
          <span className="prod-badge">RECRUITMENT PLATFORM</span>
          <h1 className="prod-main-title">
            Hire Smarter. <br />
            <span>Hire Faster.</span>
          </h1>
          <p className="prod-desc">
            Transform recruitment operations, source candidates, rank applications with AI screening, 
            and automate candidate workflows from one central dashboard.
          </p>
          <div className="prod-cta-group">
            <a href="#contact" className="prod-primary-btn">
              Request Demo <ArrowRight size={16} />
            </a>
            <a 
              href="https://hiring.gtelinq.com/" 
              className="prod-secondary-btn"
            >
              Login to Hiring Hub <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* HERO VISUAL MOCKUP */}
        <div className="prod-hero-right">
          <div className="prod-mockup-frame">
            <div className="mockup-header">
              <div className="mockup-dots">
                <div className="mockup-dot red"></div>
                <div className="mockup-dot yellow"></div>
                <div className="mockup-dot green"></div>
              </div>
              <span className="mockup-title">hiring.gtelinq.com/pipeline</span>
            </div>
            <div className="mockup-content">
              <div className="mockup-sidebar">
                <div className="sidebar-item active"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
              </div>
              <div className="mockup-main">
                <div className="mockup-bar"></div>
                <div className="mockup-cards">
                  <div className="mockup-card">
                    <h5>Active Jobs</h5>
                    <span className="mockup-card-value">14</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "65%" }}></div>
                    </div>
                  </div>
                  <div className="mockup-card">
                    <h5>Applicants</h5>
                    <span className="mockup-card-value">342</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "85%" }}></div>
                    </div>
                  </div>
                </div>
                <div className="mockup-chart-container">
                  <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)" }}>RECRUITMENT FUNNEL</span>
                  <div className="chart-bars">
                    <div className="chart-bar" style={{ height: "60px" }}></div>
                    <div className="chart-bar" style={{ height: "45px" }}></div>
                    <div className="chart-bar highlight" style={{ height: "30px" }}></div>
                    <div className="chart-bar" style={{ height: "15px" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="prod-features-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">ATS FEATURES</span>
          <h2 className="prod-section-title">Automate Your Recruitment Pipeline</h2>
        </div>
        <div className="prod-features-grid">
          {features.map((feat, idx) => (
            <div className="prod-feature-card" key={idx}>
              <div className="prod-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.description}</p>
            </div>
          ))}
        </div>
      </section>

     

      {/* FAQ */}
      <section className="prod-faq-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">FAQ</span>
          <h2 className="prod-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="prod-faq-list">
          <div className="prod-faq-item">
            <h4>Does it parse resumes in PDF format?</h4>
            <p>Yes, Hiring Hub supports parsing files in PDF, DOCX, and image formats into structured candidate objects.</p>
          </div>
          <div className="prod-faq-item">
            <h4>Can we post jobs to multiple platforms?</h4>
            <p>Yes, you can post to LinkedIn, Indeed, and Google Jobs with one click directly from our career portal module.</p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <div className="con-page-wrapper">
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default HiringHub;
